import React, { useEffect, useRef } from 'react';

interface CosmicWavesProps {
  className?: string;
  speed?: number;
  amplitude?: number;
  frequency?: number;
  starDensity?: number;
  colorShift?: number;
}

const CosmicWaves: React.FC<CosmicWavesProps> = ({ 
  className = '', 
  speed = 9, 
  amplitude = 1,
  frequency = 1,
  starDensity = 1,
  colorShift = 1
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) return;

    const vertexShaderSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragmentShaderSource = `
      precision highp float;
      uniform float u_time;
      uniform vec2 u_resolution;
      uniform float u_speed;
      uniform float u_amplitude;
      uniform float u_frequency;
      uniform float u_starDensity;
      uniform float u_colorShift;

      // Hash function for pseudo-random values
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }

      // Smooth noise function
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        float a = hash(i);
        float b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0));
        float d = hash(i + vec2(1.0, 1.0));
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
      }

      // Fractal noise (FBM)
      float fbm(vec2 p) {
        float value = 0.0;
        float amp = 0.5;
        for(int i = 0; i < 4; i++) {
          value += amp * noise(p);
          p *= 2.0;
          amp *= 0.5;
        }
        return value;
      }

      // Star field generation with twinkle
      float stars(vec2 p, float density) {
        vec2 grid = floor(p * density);
        vec2 local = fract(p * density);
        float h = hash(grid);
        if(h > 0.95) {
          float d = length(local - 0.5);
          float star = exp(-d * 20.0);
          return star * (0.5 + 0.5 * sin(u_time * 2.0 + h * 10.0));
        }
        return 0.0;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        vec2 p = uv * 2.0 - 1.0;
        p.x *= u_resolution.x / u_resolution.y;

        float time = u_time * u_speed;

        // Create flowing wave patterns - 极大幅度增加移动速度权重
        vec2 wavePos = p * u_frequency;
        wavePos.y += time * 4.0; // 显著增加纵向基础流动速度
        wavePos.x += time * 1.5; // 增加横向基础流动速度

        // Multiple wave layers - 引入更剧烈的时间变量
        float wave1 = sin(wavePos.x + cos(wavePos.y + time * 2.0) * 0.8) * u_amplitude;
        float wave2 = sin(wavePos.x * 1.3 - wavePos.y * 0.7 + time * 3.0) * u_amplitude * 0.7;
        float wave3 = sin(wavePos.x * 0.8 + wavePos.y * 1.1 - time * 2.5) * u_amplitude * 0.5;

        // Combine waves - 显著增加波浪合成权重
        float waves = (wave1 + wave2 + wave3) * 0.7;

        // Add fractal noise for organic texture
        vec2 noisePos = p * 1.5 + vec2(time * 0.8, time * 0.4);
        float noiseValue = fbm(noisePos) * 0.4;

        // Combine waves and noise
        float pattern = waves + noiseValue;

        // Cosmic spectrum colors - 使用更鲜艳的霓虹色彩
        vec3 color1 = vec3(0.1, 0.2, 0.8);  // Deep blue
        vec3 color2 = vec3(0.6, 0.1, 0.9);  // Purple
        vec3 color3 = vec3(0.29, 0.0, 0.51); // Indigo (4B0082)
        vec3 color4 = vec3(0.9, 0.3, 0.6);  // Pink

        // Color interpolation - 色彩循环随时间流动
        float colorTime = time * u_colorShift + pattern;
        vec3 finalColor;
        float t = fract(colorTime * 0.3); // 加快色彩循环切换速度
        
        if(t < 0.25) finalColor = mix(color1, color2, t * 4.0);
        else if(t < 0.5) finalColor = mix(color2, color3, (t - 0.25) * 4.0);
        else if(t < 0.75) finalColor = mix(color3, color4, (t - 0.5) * 4.0);
        else finalColor = mix(color4, color1, (t - 0.75) * 4.0);

        // Apply pattern intensity - 极大幅度增强强度和对比度，使霓虹感爆棚
        finalColor *= (0.2 + pattern * 2.5);

        // Add star field - 加快星星闪烁和移动
        float starField = stars(p + vec2(time * 0.3, time * 0.15), u_starDensity * 15.0);
        starField += stars(p * 1.5 + vec2(-time * 0.25, time * 0.12), u_starDensity * 12.0);
        finalColor += vec3(starField * 0.8);

        // Subtle glow and vignette - 增强霓虹光晕
        float glow = exp(-length(p) * 0.6) * 0.4;
        finalColor += glow * vec3(0.2, 0.4, 0.8);
        
        float vignette = 1.0 - length(uv - 0.5) * 1.2;
        finalColor *= smoothstep(0.0, 1.0, vignette);

        // 稍微压低整体亮度以保证文字可读性，但保留霓虹感
        gl_FragColor = vec4(finalColor * 0.7, 1.0);
      }
    `;

    const createShader = (gl: WebGLRenderingContext, type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const program = gl.createProgram();
    if (!program) return;

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) return;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 1, -1, -1, 1,
      -1, 1, 1, -1, 1, 1,
    ]), gl.STATIC_DRAW);

    const positionAttributeLocation = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(positionAttributeLocation);
    gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    const uniforms = {
      time: gl.getUniformLocation(program, 'u_time'),
      resolution: gl.getUniformLocation(program, 'u_resolution'),
      speed: gl.getUniformLocation(program, 'u_speed'),
      amplitude: gl.getUniformLocation(program, 'u_amplitude'),
      frequency: gl.getUniformLocation(program, 'u_frequency'),
      starDensity: gl.getUniformLocation(program, 'u_starDensity'),
      colorShift: gl.getUniformLocation(program, 'u_colorShift'),
    };

    let animationFrameId: number;
    const render = (time: number) => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }

      gl.useProgram(program);
      gl.uniform1f(uniforms.time, time * 0.001);
      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height);
      gl.uniform1f(uniforms.speed, speed);
      gl.uniform1f(uniforms.amplitude, amplitude);
      gl.uniform1f(uniforms.frequency, frequency);
      gl.uniform1f(uniforms.starDensity, starDensity);
      gl.uniform1f(uniforms.colorShift, colorShift);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      animationFrameId = requestAnimationFrame(render);
    };

    animationFrameId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [speed, amplitude, frequency, starDensity, colorShift]);

  return (
    <canvas
      ref={canvasRef}
      className={`w-full h-full ${className}`}
      style={{ display: 'block' }}
    />
  );
};

export default CosmicWaves;
